import React, { useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "./FontAwesome";
import useStyles from "./Button.styles";
import { isNullOrEmpty } from "../../utils";
import { StoreState } from "../../models/states/StoreState";
import { DefaultFocusStatus } from "../../models/states/DefaultFocusStatus";
import { setDefaultFocusStatus } from "../../redux/actions/index";

interface Props {
    className?: string;
    disabled?: boolean;
    active?: boolean;
    hidden?: boolean;
    icon?: IconProp;
    iconPosition?: "start" | "end";
    iconOnHover?: boolean;
    size?: "small" | "large" | "full" | "flex" | number;
    text?: string;
    displayText?: boolean;
    displayIcon?: boolean;
    onClick(): void;
    imageUrl?: string;
    buttonType?: string;
}

interface IconProps {
    active: boolean;
    disabled?: boolean;
    iconOnHover: boolean;
    icon: IconProp;
    iconPosition?: "start" | "end";
    size?: "small" | "large" | "full" | "flex" | number;
    textAndIcon: any;
    imageUrl?: string;
}

function getSizeClassName(classes, size) {
    if (typeof size === "number") {
        return "";
    }
    switch (size) {
        case "small":
            return classes.buttonSmall;
        case "full":
            return classes.buttonFull;
        case "flex":
            return classes.buttonFlex;
        default:
            return classes.buttonLarge;
    }
}

function ButtonIcon(props: IconProps) {
    const {
        size,
        active,
        disabled,
        icon,
        iconPosition,
        textAndIcon,
        iconOnHover,
        imageUrl,
    } = props;

    const theme = useTheme();
    const classes = useStyles({ theme, size, iconPosition, textAndIcon, iconOnHover });

    return (
        <div
            className={
                !imageUrl &&
                classnames(
                    classes.buttonIconContainer,
                    textAndIcon && iconOnHover && classes.buttonIconAnimated,
                    textAndIcon && active && iconOnHover && classes.buttonIconAnimatedHover,
                )
            }
        >
            {!imageUrl && (
                <FontAwesomeIcon
                    fixedWidth
                    className={classnames(
                        classes.buttonIcon,
                        disabled && classes.buttonIconDisabled,
                    )}
                    icon={icon}
                />
            )}
            {imageUrl && <img src={imageUrl} className={classes.pointer} />}
        </div>
    );
}

export default function Button(props: Props) {
    const {
        size,
        className,
        disabled,
        onClick,
        active,
        hidden,
        text,
        icon,
        iconPosition,
        iconOnHover,
        displayText,
        displayIcon,
        imageUrl,
        buttonType,
        ...otherProps
    } = props;

    const renderText = displayText !== false && !isNullOrEmpty(text);
    const renderIcon = displayIcon !== false && icon != null;
    const textAndIcon = renderText && renderIcon;

    const position = iconPosition || "end";

    const theme = useTheme();
    const classes = useStyles({ theme, iconPosition: position });

    const [_active, setActive] = React.useState(false);

    const isActive = active || _active;
    const buttonReference = useRef(null);
    const dispatch = useDispatch();
    const defaultFocusStatus = useSelector(
        (state: StoreState) => state.defaultFocusStatus,
    ) as DefaultFocusStatus;

    useEffect(() => {
        // Temporarily rolled back due to DEV-90066: Surveys freezing when taken on mobile (NA4 - 6.12 - S2)
        // if (
        //     buttonType &&
        //     (buttonType === "SUBMIT" || buttonType === "NEXT" || buttonType === "BACK") &&
        //     defaultFocusStatus &&
        //     defaultFocusStatus.statuses
        // ) {
        //     if (buttonType === "SUBMIT") {
        //         const submitButtonInfo = defaultFocusStatus.statuses.find((e) => {
        //             return e.elementId === "SUBMIT";
        //         });
        //         if (!submitButtonInfo) {
        //             defaultFocusStatus.statuses.push({
        //                 elementId: "SUBMIT",
        //                 elementRef: buttonReference,
        //                 focusStatus: false,
        //                 variableId: 0,
        //             });
        //             dispatch(setDefaultFocusStatus(defaultFocusStatus));
        //         }
        //     } else if (buttonType === "NEXT") {
        //         const nextButtonInfo = defaultFocusStatus.statuses.find((e) => {
        //             return e.elementId === "NEXT";
        //         });
        //         if (!nextButtonInfo) {
        //             defaultFocusStatus.statuses.push({
        //                 elementId: "NEXT",
        //                 elementRef: buttonReference,
        //                 focusStatus: false,
        //                 variableId: 0,
        //             });
        //             dispatch(setDefaultFocusStatus(defaultFocusStatus));
        //         }
        //     }
        // }
        // document.addEventListener("keydown", handleKeyPress);
        // return function cleanup() {
        //     document.removeEventListener("keydown", handleKeyPress);
        // };
    }, [buttonType]);
    // function handleKeyPress(event) {
    //     if (event.key !== "Enter") return;

    //     if (defaultFocusStatus && defaultFocusStatus.statuses) {
    //         const focusStatus = defaultFocusStatus.statuses.find((e) => {
    //             return e.focusStatus;
    //         });

    //         if (!focusStatus) return;

    //         const nextBtnInfo = defaultFocusStatus.statuses.find((e) => {
    //             return e.elementId === "NEXT";
    //         });
    //         const submitBtnInfo = defaultFocusStatus.statuses.find((e) => {
    //             return e.elementId === "SUBMIT";
    //         });

    //         if (nextBtnInfo && nextBtnInfo.elementRef && nextBtnInfo.elementRef.current) {
    //             nextBtnInfo.elementRef.current.click();
    //             // Reset the focus status
    //             defaultFocusStatus.statuses.forEach((element) => {
    //                 // eslint-disable-next-line no-param-reassign
    //                 element.focusStatus = false;
    //             });
    //         } else if (
    //             submitBtnInfo &&
    //             submitBtnInfo.elementRef &&
    //             submitBtnInfo.elementRef.current
    //         ) {
    //             submitBtnInfo.elementRef.current.click();
    //             // Reset the focus status
    //             defaultFocusStatus.statuses.forEach((element) => {
    //                 // eslint-disable-next-line no-param-reassign
    //                 element.focusStatus = false;
    //             });
    //         }
    //     }
    //     event.preventDefault();
    // }

    return (
        <button
            type="button"
            ref={buttonReference}
            aria-label={text}
            onFocus={() => setActive(true)}
            onBlur={() => setActive(false)}
            onMouseOver={() => setActive(true)}
            onMouseOut={() => setActive(false)}
            onClick={() => {
                if (disabled) {
                    return;
                }
                if (onClick) {
                    onClick();
                }
            }}
            onKeyPress={(event) => {
                if (disabled) {
                    return;
                }
                if (event.key === " " || event.key === "Enter") {
                    if (onClick) {
                        onClick();
                    }
                    event.preventDefault();

                    // When Page changes, clear the focus status information in store
                    defaultFocusStatus.statuses = [];
                    dispatch(setDefaultFocusStatus(defaultFocusStatus));
                }
            }}
            className={classnames(
                className,
                !imageUrl && classes.button,
                !imageUrl && classes.buttonBorderNone,
                !imageUrl && getSizeClassName(classes, size),
                isActive && !disabled && !imageUrl && classes.buttonActive,
                disabled && classes.buttonDisabled,
                hidden && classes.buttonHidden,
            )}
            {...otherProps}
        >
            {position === "start" && renderIcon && (
                <ButtonIcon
                    size={size}
                    icon={icon}
                    iconPosition={position}
                    iconOnHover={iconOnHover}
                    active={isActive}
                    textAndIcon={textAndIcon}
                    disabled={disabled}
                    imageUrl={imageUrl}
                />
            )}
            {renderText && !imageUrl && (
                <div
                    className={classnames(
                        classes.buttonTextContainer,
                        textAndIcon && iconOnHover && classes.buttonTextAnimated,
                        textAndIcon && isActive && iconOnHover && classes.buttonTextAnimatedHover,
                    )}
                    aria-hidden="true"
                >
                    {text}
                </div>
            )}
            {position === "end" && renderIcon && (
                <ButtonIcon
                    size={size}
                    icon={icon}
                    active={isActive}
                    iconPosition={position}
                    textAndIcon={textAndIcon}
                    disabled={disabled}
                    iconOnHover={iconOnHover}
                    imageUrl={imageUrl}
                />
            )}
        </button>
    );
}
